import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuItem,
} from "@/components/ui/DropdownMenu";
import { Separator } from "@/components/ui/Separator";
import { useDispatch } from "react-redux";
import { logout } from "@/features/auth/AuthSlice";
import { AppDispatch, RootState } from "@/app/store";
import { useNavigate } from "react-router-dom";
import { useToast } from "../UseToast";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import userimg from "@/assets/userSvg.svg";
import profileimg from "@/assets/profile-image.svg";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/Dialog";
import { IMAGES } from "@/assets/IMAGES";
import { Button } from "../Button";
import { useGetCounterByIdQuery, useUpdateCounterOccupiedStatusMutation } from "@/services/CounterApi";
import { useEffect, useState } from "react";
import { SwitchUserDialog } from "@/components/custom-component/SwitchUserDialogue";
const pageTitles = [
  { targetPath: "/admin/dashboard", title: "Dashboard" },
  { targetPath: "/admin/roles", title: "RBAC" },
  { targetPath: "/admin/staff", title: "Staff" },
  { targetPath: "/admin/counter", title: "Counter" },
  { targetPath: "/admin/service", title: "Services" },
  { targetPath: "/admin/department", title: "Departments" },
  { targetPath: "/admin/queue-monitor", title: "Live Queue Monitoring" },
  { targetPath: "/admin/queue-monitor/:id", title: "Department Queue Monitor" },
  { targetPath: "/admin/tickets", title: "All Tickets" },
  { targetPath: "/admin/kanbanview", title: "Kanban" },
  { targetPath: "/admin/task", title: "All Tasks" },
  {
    targetPath: "/admin/serve-ticket",
    title: `Serve Ticket`,
  },
  { targetPath: "/admin/form-management", title: "Form Management" },
  { targetPath: "/admin/form-builder", title: "Form Builder" },
  { targetPath: "/admin/form-submissions", title: "Form Responses" },
  { targetPath: "/admin/workflow-management", title: "Workflow Management" },
  { targetPath: "/admin/workflow-builder", title: "Workflow Builder" },
  { targetPath: "/admin/workflow-executions", title: "Workflow Executions" },
  { targetPath: "/admin/workflow-approvals", title: "Workflow Approvals" },
];

const breadcrumbs = [
  {
    targetPath: "/admin/roles",
    title: "RBAC",
    pageSetting: "System Setting",
  },
];

const Breadcrumb = ({
  currentPath,
  targetPath,
  title,
  pageSetting,
}: {
  currentPath: string;
  targetPath: string;
  title: string;
  pageSetting?: string;
}) => {
  if (!isActiveLink(currentPath, targetPath)) return null;
  return (
    <div className="text-sm">
      <span className="text-gray-400 pr-1 font-semibold text-xs slg:text-sm">
        {pageSetting}
      </span>{" "}
      <span className="text-gray-400 font-semibold ">/</span>
      <span className="pl-1 text-primary font-semibold text-sm">{title}</span>
    </div>
  );
};

const PageTitle = ({
  currentPath,
  targetPath,
  title,
}: {
  currentPath: string;
  targetPath: string;
  title: string;
}) => {
  if (!isActiveLink(currentPath, targetPath)) return null;
  return <p className="text-base slg:text-lg text-primary">{title}</p>;
};


const isActiveLink = (currentPath: string, targetPath: string) => {
  if (currentPath === targetPath) return true; // Exact match

  if (targetPath === "/admin/queue-monitor/:id") {
    // Ensure currentPath starts with "/admin/queue-monitor/" AND has an ID (more than 3 segments)
    return (
      currentPath.startsWith("/admin/queue-monitor/") &&
      currentPath.split("/").length > 3
    );
  }

  return false;
};


export const Header = () => {
  const location = useLocation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { userInfo } = useSelector((state: RootState) => state.auth);
  const dispatch = useDispatch<AppDispatch>();
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [switchUserDialogOpen, setSwitchUserDialogOpen] = useState(false);
  const [updateCounterStatus, { isLoading: isCounterLoading }] =
    useUpdateCounterOccupiedStatusMutation();

  const { data: counterData, refetch: refetchCounter } = useGetCounterByIdQuery(
    userInfo?.counter_id || 0
  );
  const handleLogout = async () => {
    // Use the existing refetch function instead of trying to create a new hook
    const currentCounterData = await refetchCounter();

    const isCurrentCounterOccupied = currentCounterData?.data?.is_occupied;
    const isOccupiedByCurrentUser =
      currentCounterData?.data?.occupied_by === userInfo?.id;

    if (
      isCurrentCounterOccupied &&
      isOccupiedByCurrentUser &&
      userInfo?.counter_id &&
      userInfo?.id
    ) {
      try {
        await updateCounterStatus({
          id: userInfo.counter_id,
          staff_id: userInfo.id,
        }).unwrap();
        // Update local storage to reflect counter is closed
        localStorage.setItem("is_occupied", "false");
        localStorage.setItem("counter_open", "false");
      } catch (error) {
        console.error("Failed to close counter during user switch:", error);
      }
    }
    dispatch(logout());
    toast({
      variant: "success",
      title: "Logout",
      description: "You are Successfully Logged Out",
    });
    navigate("/login", { replace: true });
  };

  const handleSwitchUserClick = () => {
    // Always show confirmation dialog first
    setShowConfirmationDialog(true);
  };

  // Update the handleConfirmation function:
  const handleConfirmation = (confirmed: boolean) => {
    setShowConfirmationDialog(false);

    if (confirmed) {
      // User confirmed, open the switch user dialog
      setSwitchUserDialogOpen(true);
      localStorage.setItem("userSwitchInProgress", "true");
    }
    // If canceled, do nothing
  };


  // Add this useEffect at the top level of your Header component
  useEffect(() => {
    // This function handles storage events from other tabs
    const handleStorageChange = (event: StorageEvent) => {
      // Check if localStorage was cleared in another tab
      if (event.key === null && event.storageArea === localStorage) {
        // localStorage was cleared, so log out the user in this tab too
        dispatch(logout());
        navigate("/login", { replace: true });
      }

      // Check for userSwitchInProgress specifically
      if (event.key === "userSwitchInProgress" && event.newValue === "true") {
        // Another tab triggered user switch, log out this tab
        dispatch(logout());
        navigate("/login", { replace: true });
      }
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [dispatch, navigate]);

  // Add this useEffect right after the one above

  useEffect(() => {
    // Clear userSwitchInProgress on component mount/page load
    const switchInProgress = localStorage.getItem("userSwitchInProgress");
    if (switchInProgress) {
      localStorage.removeItem("userSwitchInProgress");
    }
  }, []);


  return (
    <header className="font-poppins sticky top-0 z-30 flex h-16 items-center justify-between border-b bg-white px-4 shadow-sm">
      <div className="flex w-full justify-between items-center gap-4">
        <div className="flex flex-col h-full items-start w-full max-w-[calc(100%-280px)]">
          <h1 className="text-2xl font-semibold ">
            {pageTitles.map(({ targetPath, title }, index) => (
              <PageTitle
                key={index}
                currentPath={location.pathname}
                targetPath={targetPath}
                title={title}
              />
            ))}
          </h1>
          <div className="flex text-nowrap ">
            {breadcrumbs.map(({ targetPath, title, pageSetting }, index) => (
              <Breadcrumb
                key={index}
                currentPath={location.pathname}
                targetPath={targetPath}
                title={title}
                pageSetting={pageSetting}
              />
            ))}
          </div>
        </div>

        <div className="flex flex-row justify-end items-center gap-3 px-2">
          <button
            onClick={handleSwitchUserClick}
            className="bg-primary text-white flex flex-row  rounded-md px-2 py-2 text-sm font-semibold hover:bg-primary  hover:text-black transition duration-200 ease-in-out"
          >
            <div className="flex items-center gap-2 text-nowrap">
              <i className="fa-solid fa-user"></i> Switch User
            </div>
          </button>
          {/* <Separator orientation="vertical" className="h-10" /> */}
          {/* <div className="hover:cursor-pointer">
            <img src={IMAGES.notification_bell} className="size-6" alt="" />
          </div> */}
          <Separator orientation="vertical" className="h-10 mr-2" />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <div className="justify-center cursor-pointer items-center flex gap-3">
                <img
                  src={profileimg}
                  width="32"
                  height="32"
                  className="rounded-full"
                  alt="Avatar"
                />
                <div>
                  <h1 className="text-[#212529] capitalize text-sm">
                    {userInfo?.first_name}
                  </h1>
                  <p className="text-[#212529] capitalize text-xs text-nowrap">
                    business portal
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="rounded-full w-2 px-2.5"
                >
                  <i className="fa-solid fa-caret-down "></i>
                </Button>
              </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleLogout}>Logout</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>


      <Dialog
        open={showConfirmationDialog}
        onOpenChange={setShowConfirmationDialog}
      >
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Switch User Confirmation</DialogTitle>
            <DialogDescription>
              Are you sure you want to switch to a different user? you will be
              logged out from the current session.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end space-x-2 pt-4">
            <Button
              variant="outline"
              className="w-fit"
              onClick={() => {
                handleConfirmation(false);
                localStorage.removeItem("userSwitchInProgress");
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                // Clear localStorage
                localStorage.clear();
                handleConfirmation(true);
              }}
            >
              Continue
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Switch User Dialog */}
      <SwitchUserDialog
        open={switchUserDialogOpen}
        onOpenChange={(open) => {
          setSwitchUserDialogOpen(open);
          if (!open) {
            localStorage.removeItem("switchUserDialogOpen");
          }
        }}
      />
    </header>
  );
};
